import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AddInIds from "@/constants/AddInIds";
import { useAddinData } from "@/hooks/addins/useAddinData";
import { useRemoveAddInByMatch } from "@/hooks/addins/useRemoveAddIn";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";
import { useGroups } from "@/hooks/groups/useGroups";
import { DiagnosticAddinData } from "@/pages/diagnostic-edit/models/DiagnosticAddinData";

interface Props {
  diagnosticId: string;
}

const PRIORITY_OPTIONS = [
  { value: 0, label: "N/A" },
  { value: 1, label: "Info" },
  { value: 2, label: "Low" },
  { value: 3, label: "Medium" },
  { value: 4, label: "High" },
];

export function DiagnosticOverrideDetail({ diagnosticId }: Props) {
  const { t } = useTranslation();
  const { data: allAddinData = [] } = useAddinData<DiagnosticAddinData>(AddInIds.diagnosticData);
  const { data: allGroups = [] } = useGroups();
  const updateAddin = useUpdateAddIn<DiagnosticAddinData>(AddInIds.diagnosticData, [
    "diagnosticId",
    "groupId",
  ]);
  const removeAddin = useRemoveAddInByMatch<DiagnosticAddinData>(AddInIds.diagnosticData);

  const overrides = useMemo(
    () => allAddinData.filter(a => a.diagnosticId === diagnosticId && !!a.groupId),
    [allAddinData, diagnosticId],
  );

  const usedGroupIds = useMemo(() => new Set(overrides.map(o => o.groupId)), [overrides]);
  const availableGroups = useMemo(
    () => allGroups.filter(g => g.id && !usedGroupIds.has(g.id)),
    [allGroups, usedGroupIds],
  );

  const groupNameById = useMemo(() => {
    const m: Record<string, string> = {};
    allGroups.forEach(g => {
      if (g.id) m[g.id] = g.name ?? g.id;
    });
    return m;
  }, [allGroups]);

  const [pickedGroupId, setPickedGroupId] = useState<string | null>(null);

  const addOverride = () => {
    if (!pickedGroupId) return;
    updateAddin.mutate({
      diagnosticId,
      groupId: pickedGroupId,
      priority: 0,
      description: "",
      instruction: "",
    });
    setPickedGroupId(null);
  };

  const updateField = (override: DiagnosticAddinData, patch: Partial<DiagnosticAddinData>) => {
    updateAddin.mutate({ ...override, ...patch });
  };

  return (
    <Box sx={{ p: 2, backgroundColor: "background.default" }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t("diagnosticEdit.overrides.title")}
      </Typography>

      {overrides.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("diagnosticEdit.overrides.noOverrides")}
        </Typography>
      ) : (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 200 }}>
                {t("diagnosticEdit.overrides.groupColumn")}
              </TableCell>
              <TableCell sx={{ width: 160 }}>{t("diagnosticEdit.priotity")}</TableCell>
              <TableCell>{t("diagnosticEdit.description")}</TableCell>
              <TableCell>{t("faultOverview.instruction")}</TableCell>
              <TableCell sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {overrides.map(override => {
              const label =
                groupNameById[override.groupId ?? ""] ??
                t("diagnosticEdit.overrides.deletedGroup");
              return (
                <TableRow key={`${override.diagnosticId}-${override.groupId}`}>
                  <TableCell>{label}</TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={override.priority}
                      onChange={e =>
                        updateField(override, { priority: Number(e.target.value) })
                      }
                    >
                      {PRIORITY_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      defaultValue={override.description}
                      onBlur={e => {
                        if (e.target.value !== override.description) {
                          updateField(override, { description: e.target.value });
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      defaultValue={override.instruction}
                      onBlur={e => {
                        if (e.target.value !== override.instruction) {
                          updateField(override, { instruction: e.target.value });
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label={t("diagnosticEdit.overrides.remove")}
                      onClick={() =>
                        removeAddin.mutate({
                          matcher: { diagnosticId, groupId: override.groupId },
                        })
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        <Autocomplete
          size="small"
          sx={{ minWidth: 280 }}
          options={availableGroups}
          getOptionKey={option => option.id ?? ""}
          getOptionLabel={option => option.name ?? ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={availableGroups.find(g => g.id === pickedGroupId) ?? null}
          onChange={(_, value) => setPickedGroupId(value?.id ?? null)}
          renderInput={params => (
            <TextField
              {...params}
              placeholder={t("diagnosticEdit.overrides.selectGroup")}
            />
          )}
        />
        <Button variant="outlined" size="small" disabled={!pickedGroupId} onClick={addOverride}>
          {t("diagnosticEdit.overrides.add")}
        </Button>
      </Stack>
    </Box>
  );
}
